import {
  type BackendSort,
  type ChannelAlias,
  useHotSaleInfinite,
  useProductImageSearchInfinite,
  useProductSearch,
} from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CameraIcon } from "../components/CameraIcon";
import { TgBottomNav } from "../components/TgBottomNav";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { EVENTS, trackEvent } from "../lib/analytics";
import { parseChannel, SELECTABLE_CHANNELS } from "../lib/catalog-channel";
import { useImageSearchPicker } from "../lib/image-search";

const TILE_BG = [
  "linear-gradient(135deg,#FFE5DD,#FFD3C2)",
  "linear-gradient(135deg,#E0F7F1,#B8E8DD)",
  "linear-gradient(135deg,#EFEBFF,#D7CCFF)",
  "linear-gradient(135deg,#FFF8DA,#FFE5A0)",
  "linear-gradient(135deg,#FFE0E9,#FFC8D6)",
  "linear-gradient(135deg,#FFF0EB,#FFD3C2)",
] as const;

// Platform chips, ordered 1688 · Taobao · Weidian (from SELECTABLE_CHANNELS) to
// match the web catalog sub-header (#198). The legacy "全部/Все (all)" chip was
// removed because "all" silently searched Taobao (#206/#213); selection now
// maps 1:1 to a real backend channel, so a chip is always highlighted. Labels
// are platform proper nouns (1688 / Taobao / Weidian), not translatable UI copy.
const CHANNEL_LABELS: Partial<Record<ChannelAlias, string>> = {
  "1688": "1688",
  taobao: "Taobao",
  weidian: "Weidian",
};

// Sort options shown in the result meta bar (hidden in image-search mode)
const SORT_CYCLE: BackendSort[] = ["default", "price_asc", "price_desc", "sales_desc"];

function getParam(search: string, key: string): string {
  const u = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return u.get(key) ?? "";
}

// Hot-sale threshold: show badge when monthSold is meaningfully high.
const HOT_SALE_THRESHOLD = 50;

/**
 * Catalog tab · 1:1 port of `mini-catalog.html`. Search bar + channel chips
 * + 2-col grid. `imageId` query param flips to image-search mode (CatHead
 * gating mirrors the web catalog: sort/filter hidden, banner shown).
 *
 * Wave-3 additions (this file):
 *  - ① Hot-sale badge (monthSold >= threshold) · tagList[0] sticker
 *  - ② Sort button cycling price_asc / price_desc / sales_desc / default
 *  - ③ Infinite scroll via IntersectionObserver + fetchNextPage
 *  - ④ No-keyword mode shows useHotSaleInfinite (hot popular products)
 *  - ⑤ Sticky search bar (position:sticky, top aligned after TgHeader)
 *  - ⑥ Min-price hint in result meta row (cheapest record on current page)
 *  - ⑦ Duplicate camera button removed from inside search form
 */
export function CatalogPage() {
  const { t } = useTranslation("catalog");
  const navigate = useNavigate();
  const searchStr = useRouterState({ select: (s) => s.location.searchStr ?? "" });
  const initialKw = getParam(searchStr, "keyword");
  const imageId = getParam(searchStr, "imageId");
  // Resolve absent / empty / legacy "all" / unsupported ?channel= → 1688
  // (#206/#213); a fresh entry + keyword search no longer silently runs on
  // Taobao. The channel is always a real ChannelAlias, so a chip is always
  // selected — no dead "all" placeholder.
  const initialCh = parseChannel(getParam(searchStr, "channel"));

  const [draftKw, setDraftKw] = useState(initialKw);
  const [channel, setChannel] = useState<ChannelAlias>(initialCh);
  const [sort, setSort] = useState<BackendSort>("default");
  const activeChannel: ChannelAlias = channel;
  const imagePicker = useImageSearchPicker();

  // Sync draft keyword when URL keyword changes (e.g. back-nav)
  useEffect(() => {
    setDraftKw(initialKw);
  }, [initialKw]);

  // ── Data sources ──────────────────────────────────────────────────────────
  const isImage = !!imageId;
  const hasKeyword = !!initialKw;

  const searchQ = useProductSearch({
    channel: activeChannel,
    keyword: initialKw,
    pageSize: 20,
    ...(sort !== "default" ? { sort } : {}),
  });

  const imageQ = useProductImageSearchInfinite({
    imageId,
    channel: activeChannel,
    pageSize: 20,
  });

  // No-keyword mode: show hot-sale products instead of empty state (#feature)
  const hotQ = useHotSaleInfinite({
    channel: activeChannel,
    pageSize: 20,
    ...(sort !== "default" ? { sort } : {}),
  });

  // Active query: image > keyword-search > hot-sale
  const active = isImage ? imageQ : hasKeyword ? searchQ : hotQ;
  const records = useMemo(
    () => (active.data?.pages ?? []).flatMap((p) => p.records),
    [active.data],
  );
  const total = active.data?.pages?.[0]?.total ?? 0;

  // Cheapest price visible in the current result set (for meta row)
  const minPrice = useMemo(
    () =>
      records.length > 0
        ? Math.min(...records.map((r) => r.priceRubKopecks))
        : null,
    [records],
  );

  // Track search event when keyword results arrive from the real API.
  // Fires once per (initialKw, total) pair; skips image-search mode.
  useEffect(() => {
    if (!isImage && hasKeyword && !searchQ.isLoading && searchQ.data) {
      trackEvent(EVENTS.SEARCH, { query: initialKw, results_count: total });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKw, total, isImage, searchQ.isLoading]);

  // TODO(yandex-metrica/task-18): fire image_search once real imageId pipeline
  // passes source metadata (upload | paste | camera) from the picker result.
  // trackEvent(EVENTS.IMAGE_SEARCH, { source: <ImageSearchSource>, results_count: total });

  // ── Infinite scroll sentinel ──────────────────────────────────────────────
  const sentinelRef = useRef<HTMLDivElement>(null);
  const fetchNextPage = active.fetchNextPage;
  const hasNextPage = active.hasNextPage;
  const isFetchingNextPage = active.isFetchingNextPage;

  const handleSentinel = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(handleSentinel, { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [handleSentinel]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = draftKw.trim();
    if (!q) return;
    setSort("default");
    navigate({
      to: "/catalog" as never,
      search: { keyword: q, channel } as never,
    });
  };

  const cycleSort = () => {
    const next = SORT_CYCLE[(SORT_CYCLE.indexOf(sort) + 1) % SORT_CYCLE.length];
    setSort(next ?? "default");
  };

  const sortLabel = (() => {
    if (sort === "price_asc") return t("sort.price_asc");
    if (sort === "price_desc") return t("sort.price_desc");
    if (sort === "sales_desc") return t("sort.sales_desc");
    return t("sort.relevance");
  })();

  return (
    <TgPage tabbar>
      {/* TgHeader: camera only in header right, NOT duplicated in search form */}
      <TgHeader
        title={t("breadcrumb.catalog")}
        back
        right={
          <button
            type="button"
            onClick={imagePicker.open}
            disabled={imagePicker.isUploading}
            style={{
              background: "transparent",
              border: 0,
              cursor: "pointer",
              color: "var(--color-primary-fill)",
              padding: "4px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <CameraIcon size={18} />
          </button>
        }
      />

      {/* ⑤ Sticky search bar — sticks just below TgHeader (88px) */}
      <div
        style={{
          padding: "10px 12px",
          background: "#fff",
          borderBottom: "1px solid var(--color-border)",
          position: "sticky",
          top: 88,
          zIndex: 8,
        }}
      >
        {/* Search form — camera button removed here, kept only in TgHeader ⑦ */}
        <form
          className="tg-search"
          onSubmit={onSubmit}
          style={{ padding: "5px 5px 5px 12px", display: "flex", alignItems: "center", gap: 4 }}
        >
          <span style={{ color: "var(--color-text-muted)", flexShrink: 0 }}>🔍</span>
          <input
            value={draftKw}
            onChange={(e) => setDraftKw(e.target.value)}
            placeholder={t("subsearch.placeholder")}
            style={{ flex: 1 }}
          />
          <button type="submit" style={{ flexShrink: 0 }}>
            {t("subsearch.submit")}
          </button>
        </form>
        {/* Platform chips */}
        <div
          style={{
            display: "flex",
            gap: 4,
            marginTop: 8,
            overflowX: "auto",
            padding: "2px 0",
          }}
        >
          {SELECTABLE_CHANNELS.map((k) => {
            const isActive = channel === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setChannel(k);
                  // #678: switching platform searches the search box's current
                  // text, not the previously-submitted keyword. If the user
                  // typed (without submitting) a new term, push it into the URL
                  // keyword so searchQ refetches the typed term on platform k.
                  const q = draftKw.trim();
                  if (q && q !== initialKw) {
                    navigate({
                      to: "/catalog" as never,
                      search: { keyword: q, channel: k } as never,
                    });
                  }
                }}
                style={{
                  background: isActive ? "var(--color-text)" : "transparent",
                  color: isActive ? "#fff" : "var(--color-text-muted)",
                  padding: "4px 10px",
                  borderRadius: 8,
                  fontSize: 10,
                  fontWeight: 700,
                  border: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {CHANNEL_LABELS[k] ?? k}
              </button>
            );
          })}
        </div>
      </div>

      {/* Result meta row: total + min-price ⑥ + sort button ② */}
      {/* Shown when we have any active query (keyword, image, or hot-sale) */}
      <div
        style={{
          padding: "10px 12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: 11,
          color: "var(--color-text-muted)",
          background: "#fff",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <span>
          {isImage ? (
            <span style={{ color: "var(--color-primary-fill)", fontWeight: 700 }}>
              📷 {t("image_search.title")}
            </span>
          ) : hasKeyword ? (
            <>
              {t("head.meta_found")}{" "}
              <strong style={{ color: "var(--color-text)" }}>{total}</strong>
              {minPrice !== null && (
                <span style={{ marginLeft: 4 }}>
                  · {t("pricebar.from")} {formatPrice(minPrice, "ru-RU", "RUB")}
                </span>
              )}
            </>
          ) : (
            <strong style={{ color: "var(--color-text)" }}>{t("head.title_hot")}</strong>
          )}
        </span>
        {/* ② Sort button — hidden in image-search mode (backend doesn't support it) */}
        {!isImage && (
          <button
            type="button"
            onClick={cycleSort}
            style={{
              background: "transparent",
              border: 0,
              color: sort !== "default" ? "var(--color-primary-fill)" : "var(--color-text)",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              padding: "2px 0",
            }}
          >
            {sortLabel} ▾
          </button>
        )}
      </div>

      {/* ③ Product grid with infinite scroll */}
      <div
        style={{
          padding: "10px 12px",
          flex: 1,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          alignContent: "start",
        }}
      >
        {records.map((p, i) => {
          // ① Badges: hot-sale (monthSold) · tagList sticker
          const isHot = (p.monthSold ?? 0) >= HOT_SALE_THRESHOLD;
          const sticker = p.tagList && p.tagList.length > 0 ? p.tagList[0] : null;

          return (
            <Link
              key={`${p.channel}-${p.spuNo}-${i}`}
              to={"/product/$channel/$spuNo" as never}
              params={{ channel: p.channel, spuNo: p.spuNo } as never}
              style={{
                background: "#fff",
                border: "1px solid var(--color-border)",
                borderRadius: 10,
                padding: 6,
                display: "flex",
                flexDirection: "column",
                gap: 5,
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <div
                style={{
                  aspectRatio: "1",
                  background: TILE_BG[i % TILE_BG.length],
                  borderRadius: 8,
                  display: "grid",
                  placeItems: "center",
                  fontSize: 36,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {p.imageUrl ? (
                  <img
                    src={p.imageUrl}
                    alt={p.title}
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  "🛍️"
                )}
                {/* ① Sticker from tagList (top-left) */}
                {sticker && (
                  <span
                    style={{
                      position: "absolute",
                      top: 4,
                      left: 4,
                      background: "var(--color-cta)",
                      color: "#fff",
                      fontSize: 8,
                      fontWeight: 900,
                      padding: "1px 5px",
                      borderRadius: 3,
                    }}
                  >
                    {sticker}
                  </span>
                )}
                {/* ① Hot badge (top-left, only if no tagList sticker occupying top-left) */}
                {!sticker && isHot && (
                  <span
                    style={{
                      position: "absolute",
                      top: 4,
                      left: 4,
                      background: "var(--color-cta)",
                      color: "#fff",
                      fontSize: 8,
                      fontWeight: 900,
                      padding: "1px 5px",
                      borderRadius: 3,
                    }}
                  >
                    🔥
                  </span>
                )}
                {/* Platform label (bottom-left) */}
                <span
                  style={{
                    position: "absolute",
                    bottom: 4,
                    left: 4,
                    background: "rgba(15,31,26,.7)",
                    color: "#fff",
                    fontSize: 8,
                    fontWeight: 700,
                    padding: "1px 5px",
                    borderRadius: 3,
                  }}
                >
                  {p.channel}
                </span>
              </div>
              <div
                style={{
                  fontSize: 11,
                  lineHeight: 1.3,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {p.shortTitle || p.title}
              </div>
              {/* ⑥ Price — uses priceRubKopecks (always the min/starting price from backend) */}
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 13,
                  fontWeight: 900,
                  color: "var(--color-price)",
                }}
              >
                {formatPrice(p.priceRubKopecks, "ru-RU", "RUB")}
              </div>
            </Link>
          );
        })}

        {/* Loading state */}
        {active.isLoading && (
          <div
            style={{
              gridColumn: "span 2",
              textAlign: "center",
              padding: "32px 0",
              color: "var(--color-text-muted)",
              fontSize: 13,
            }}
          >
            {t("head.meta_loading")}
          </div>
        )}

        {/* Empty state */}
        {records.length === 0 && !active.isLoading && (
          <div style={{ gridColumn: "span 2" }}>
            <TgEmpty
              icon="🔍"
              title={hasKeyword || isImage ? t("state.empty_t") : t("state.no_keyword_t")}
              hint={hasKeyword || isImage ? t("state.empty_s") : t("state.no_keyword_s")}
            />
          </div>
        )}

        {/* ③ Infinite scroll sentinel — placed inside grid so it's part of the flow */}
        <div ref={sentinelRef} style={{ gridColumn: "span 2", height: 1 }} />

        {/* Load-more status */}
        {isFetchingNextPage && (
          <div
            style={{
              gridColumn: "span 2",
              textAlign: "center",
              padding: "12px 0",
              color: "var(--color-text-muted)",
              fontSize: 12,
            }}
          >
            {t("pager.loading_more")}
          </div>
        )}

        {/* End of list */}
        {!hasNextPage && records.length > 0 && !active.isLoading && (
          <div
            style={{
              gridColumn: "span 2",
              textAlign: "center",
              padding: "12px 0",
              color: "var(--color-text-muted)",
              fontSize: 11,
            }}
          >
            {t("pager.end_of_list", { total })}
          </div>
        )}
      </div>

      <TgBottomNav />
    </TgPage>
  );
}
